import {
  CompletedMoveRequestDefaultDisplayColumns,
  CompletedMoveRequestDisplayColumns,
  DockActivityDefaultDisplayColumns,
  DockActivityDisplayColumns,
  DropTrailerDefaultDisplayColumns,
  DropTrailerDisplayColumns,
  OnComplexDefaultDisplayColumns,
  OnComplexDisplayColumns,
  SiteSettingsModel,
} from "src/app/api/api-types";

export class DisplayColumnsPreferences<T extends DisplayColumnsPreferencesKey> {
  private constructor({
    defaultDisplayColumns,
    customDisplayColumns,
  }: {
    defaultDisplayColumns: SitePreferencesDefaultProperties[T];
    customDisplayColumns: SitePreferencesCustomProperties[T];
  }) {
    this.defaultDisplayColumns = defaultDisplayColumns;
    this.customDisplayColumns = customDisplayColumns;
  }

  public readonly defaultDisplayColumns: SitePreferencesDefaultProperties[T];
  public readonly customDisplayColumns: SitePreferencesCustomProperties[T];

  public static create<T extends DisplayColumnsPreferencesKey>(
    preferencesKey: T,
    {
      defaultDisplayColumns,
      customDisplayColumns,
    }: {
      defaultDisplayColumns: SitePreferencesDefaultProperties[T];
      customDisplayColumns: SitePreferencesCustomProperties[T];
    },
  ): DisplayColumnsPreferences<T> {
    return new DisplayColumnsPreferences<typeof preferencesKey>({
      defaultDisplayColumns,
      customDisplayColumns,
    });
  }

  public static deserialize<K extends DisplayColumnsPreferencesKey>(
    data: SiteSettingsModel["sitePreferences"],
    preferencesKey: K,
  ): DisplayColumnsPreferences<K> {
    const defaultDisplayColumnsKey: keyof SitePreferencesDefaultPropertiesNames = `${preferencesKey}${defaultDisplayColumnsName}`;
    const customDisplayColumnsKey: keyof SitePreferencesCustomPropertiesNames = `${preferencesKey}${customDisplayColumnsName}`;

    const defaultDisplayColumns =
      data &&
      (data[defaultDisplayColumnsKey] as SitePreferencesDefaultProperties[K]);
    const customDisplayColumns =
      data &&
      (data[customDisplayColumnsKey] as SitePreferencesCustomProperties[K]);

    return new DisplayColumnsPreferences<typeof preferencesKey>({
      customDisplayColumns,
      defaultDisplayColumns,
    });
  }

  public serialize<K extends DisplayColumnsPreferencesKey>(
    preferenceKey: K,
  ): DisplayColumnsPreferencesMap[K] {
    const defaultDisplayColumnsKey: keyof SitePreferencesDefaultPropertiesNames = `${preferenceKey}${defaultDisplayColumnsName}`;
    const customDisplayColumnsKey: keyof SitePreferencesCustomPropertiesNames = `${preferenceKey}${customDisplayColumnsName}`;

    // typescript doesn't know that the keys are the same because they are concatenated
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
      [defaultDisplayColumnsKey]: this.defaultDisplayColumns,
      [customDisplayColumnsKey]: this.customDisplayColumns,
    } as DisplayColumnsPreferencesMap[K];
  }
}

export enum DisplayColumnsPreferencesKey {
  ON_COMPLEX = "dashboard",
  DOCK_ACTIVITY = "dockActivity",
  DROP_TRAILER = "dropTrailer",
  COMPLETED_MOVE_REQUEST = "completedMoveRequest",
}

const defaultDisplayColumnsName = "DefaultDisplayColumns";
const customDisplayColumnsName = "CustomDisplayColumns";

interface SitePreferencesDefaultProperties {
  dashboard: OnComplexDefaultDisplayColumns;
  dockActivity: DockActivityDefaultDisplayColumns;
  dropTrailer: DropTrailerDefaultDisplayColumns;
  completedMoveRequest: CompletedMoveRequestDefaultDisplayColumns;
}

interface SitePreferencesCustomProperties {
  dashboard: OnComplexDisplayColumns;
  dockActivity: DockActivityDisplayColumns;
  dropTrailer: DropTrailerDisplayColumns;
  completedMoveRequest: CompletedMoveRequestDisplayColumns;
}

interface SitePreferencesDefaultPropertiesNames {
  dashboardDefaultDisplayColumns: OnComplexDefaultDisplayColumns;
  dockActivityDefaultDisplayColumns: DockActivityDefaultDisplayColumns;
  dropTrailerDefaultDisplayColumns: DropTrailerDefaultDisplayColumns;
  completedMoveRequestDefaultDisplayColumns: CompletedMoveRequestDefaultDisplayColumns;
}

interface SitePreferencesCustomPropertiesNames {
  dashboardCustomDisplayColumns: OnComplexDisplayColumns;
  dockActivityCustomDisplayColumns: DockActivityDisplayColumns;
  dropTrailerCustomDisplayColumns: DropTrailerDisplayColumns;
  completedMoveRequestCustomDisplayColumns: CompletedMoveRequestDisplayColumns;
}

interface DisplayColumnsPreferencesMap {
  [DisplayColumnsPreferencesKey.ON_COMPLEX]: {
    dashboardDefaultDisplayColumns: OnComplexDefaultDisplayColumns;
    dashboardCustomDisplayColumns: OnComplexDisplayColumns;
  };
  [DisplayColumnsPreferencesKey.DOCK_ACTIVITY]: {
    dockActivityDefaultDisplayColumns: DockActivityDefaultDisplayColumns;
    dockActivityCustomDisplayColumns: DockActivityDisplayColumns;
  };
  [DisplayColumnsPreferencesKey.DROP_TRAILER]: {
    dropTrailerDefaultDisplayColumns: DropTrailerDefaultDisplayColumns;
    dropTrailerCustomDisplayColumns: DropTrailerDisplayColumns;
  };
  [DisplayColumnsPreferencesKey.COMPLETED_MOVE_REQUEST]: {
    completedMoveRequestDefaultDisplayColumns: CompletedMoveRequestDefaultDisplayColumns;
    completedMoveRequestCustomDisplayColumns: CompletedMoveRequestDisplayColumns;
  };
}
